import type { AnyAction } from '@reduxjs/toolkit';
import * as appActions from 'Actions/app';
import * as repertoireActions from 'Actions/repertoire';
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import { call, debounce, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { actionTypes } from 'Types';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import Logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';
import {
  accountDefinitions,
  fromAccountSettingsPayload,
  isAccountCoordinate,
  toAccountSettingsPayload,
  type AccountSettingsPayload,
} from './accountSettings';
import { currentDeviceType } from './deviceType';
import { builtInDefault } from './resolveSetting';
import { bindingFor } from './selectSetting';
import {
  accountSettingsConfirmed,
  accountSettingsLoaded,
  accountSettingsQueued,
  accountSettingsRefused,
  layerCoordinate,
  selectPendingSettingWrites,
  selectRefusedSettingWrite,
  selectSettingLayerValue,
  selectSyncedSettingValue,
  settingLayerValueSet,
  type SettingCoordinate,
  type SettingCoordinateValue,
  type SetSettingLayerValuePayload,
} from './settingsLayersSlice';
import type { PlayerLayer, SettingDefinition, SettingsState } from './settingTypes';

/**
 * Settings that follow the account (`USE_ACCOUNT_SETTINGS`).
 *
 * - At sign-in the account's values are loaded from `/user-settings`. A value
 *   with a store of its own on the device (a binding: finger numbers in
 *   mainAppReducer, a tier's tempo in tempoValues) is put there; the rest go
 *   to `settingsLayers`. A change made on this device and not yet sent keeps
 *   the newer local value.
 * - Each player change, whether a screen dispatched its usual action or wrote
 *   through the model, is queued and sent shortly after the last change.
 *   Today's screens are unchanged: the sync watches the actions they dispatch.
 * - This device keeps a copy of every value (the bound stores, and
 *   `settingsLayers`, both redux-persisted), so the app works offline; queued
 *   changes are sent at the next change or sign-in.
 * - The account store takes a save all or none. A failure a later try can fix
 *   (no answer, a 5xx, a 401 the token refresh did not fix, a 403, a 429) keeps
 *   every change queued and tries again after a growing wait. A refusal no try
 *   can fix (400 or 422) is narrowed down by splitting the batch: the halves
 *   it takes are saved, and a value refused on its own is set aside (kept on
 *   this device, logged, not sent again unless the player changes it), so one
 *   refused value never blocks every later save.
 * - Without the endpoint (a 404 from an older backend), nothing is sent and
 *   the app runs on this device's values, as before.
 */

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
export const ACCOUNT_SETTINGS_URL = `${baseUrl}user-settings`;

/** Wait this long after the last change before saving (a dragged slider sends once). */
export const ACCOUNT_SETTINGS_SAVE_DELAY_MS = 800;

const HYDRATION = 'accountSettingsHydration';

/** An action that puts a loaded value into a store: not a player change, so never sent back. */
export const asAccountHydration = (action: AnyAction): AnyAction => ({
  ...action,
  meta: { ...(action.meta ?? {}), [HYDRATION]: true },
});

const isAccountHydration = (action: AnyAction): boolean =>
  Boolean((action.meta as Record<string, unknown> | undefined)?.[HYDRATION]);

const isOn = (): boolean => isFeatureEnabled(FeatureFlag.USE_ACCOUNT_SETTINGS);

// False once the backend answered that it has no account store, until the next sign-in.
let accountStoreAvailable = true;

const isNotFound = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

/**
 * The answers no later try of the same values can fix: the account store's
 * per-value checks (400, including a piece or level that does not exist) and
 * the request pipe (422). Everything else (no answer, 5xx, 401, 403, 408, 429)
 * is about the moment or the whole request, not a value, so it is retried.
 */
const PERMANENT_REFUSAL_STATUSES: ReadonlySet<number> = new Set([400, 422]);

const permanentRefusalStatus = (error: unknown): number | undefined => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && PERMANENT_REFUSAL_STATUSES.has(status) ? status : undefined;
};

/** The waits before each retry after a failure a later try can fix; then the next change or sign-in. */
export const ACCOUNT_SETTINGS_RETRY_DELAYS_MS: readonly number[] = [
  5_000, 30_000, 120_000, 600_000,
];

const ACCOUNT_SETTINGS_RETRY_SCHEDULED = 'accountSettings/retryScheduled';

const retryScheduled = (delayMs: number): AnyAction => ({
  type: ACCOUNT_SETTINGS_RETRY_SCHEDULED,
  payload: { delayMs },
});

// Failures in a row that a later try can fix, since the last save that went through or sign-in.
let retryableFailures = 0;

// ---------------------------------------------------------------------------
// Which actions write which account setting
// ---------------------------------------------------------------------------

/** One existing action that writes a setting the account keeps, and how to place its value. */
export interface WatchedSettingAction {
  definition: SettingDefinition<unknown>;
  actionType: string;
  coordinateOf(action: AnyAction): SettingCoordinate | undefined;
}

const deviceTypeOf = (definition: SettingDefinition<unknown>) =>
  definition.residence === 'device_type' ? { deviceType: currentDeviceType() } : {};

const watchedActionsOf = (definition: SettingDefinition<unknown>): WatchedSettingAction[] => {
  const bindings = definition.bindings;
  const global = bindings?.global
    ? [
        {
          definition,
          actionType: bindings.global.write(definition.defaultValue).type,
          coordinateOf: () => layerCoordinate(definition.key, 'global', deviceTypeOf(definition)),
        },
      ]
    : [];
  const modes = Object.entries(bindings?.mode ?? {}).map(([mode, binding]) => ({
    definition,
    actionType: binding.write(definition.defaultValue).type,
    coordinateOf: () =>
      layerCoordinate(definition.key, 'mode', {
        mode: mode as SettingCoordinate['mode'],
        ...deviceTypeOf(definition),
      }),
  }));
  const instances = Object.entries(bindings?.instance ?? {}).map(([mode, binding]) => ({
    definition,
    actionType: binding.actionType,
    coordinateOf: (action: AnyAction) => {
      const instanceId = binding.instanceIdOf(action);
      return instanceId === undefined
        ? undefined
        : layerCoordinate(definition.key, 'instance', {
            mode: mode as SettingCoordinate['mode'],
            instanceId,
          });
    },
  }));
  // A layer the account does not keep (sight reading's old transposition column)
  // is watched too, and left out when its change is queued (isAccountCoordinate).
  return [...global, ...modes, ...instances];
};

/** Every existing action the sync watches, from the registry. */
export const watchedSettingActions = (): WatchedSettingAction[] =>
  accountDefinitions().flatMap(watchedActionsOf);

// ---------------------------------------------------------------------------
// Reading a layer's current value
// ---------------------------------------------------------------------------

const contextOf = (coordinate: SettingCoordinate) => ({
  ...(coordinate.mode ? { mode: coordinate.mode } : {}),
  ...(coordinate.instanceId ? { instanceId: coordinate.instanceId } : {}),
  ...(coordinate.deviceType ? { deviceType: coordinate.deviceType } : {}),
});

/** The value held at a coordinate on this device: its bound store, or settingsLayers. */
export const deviceValueAt = (
  state: SettingsState,
  definition: SettingDefinition<unknown>,
  coordinate: SettingCoordinate,
): unknown => {
  const binding = bindingFor(definition, coordinate.layer, contextOf(coordinate));
  return binding ? binding.select(state) : selectSettingLayerValue(state, coordinate);
};

/**
 * Whether a change at a coordinate needs sending: it differs from what the
 * account last confirmed, or another change is already waiting. A value the
 * account has never held that equals the built-in default is not sent (a
 * screen resetting to the default on open is not the player's choice).
 */
export const needsSending = (
  state: SettingsState,
  definition: SettingDefinition<unknown>,
  coordinate: SettingCoordinate,
  value: unknown,
): boolean => {
  if (!isAccountCoordinate(definition, coordinate)) return false;
  const pending = selectPendingSettingWrites(state).some(
    (write) => write.coordinate.key === coordinate.key && isEqual(write.coordinate, coordinate),
  );
  if (pending) return true;
  // A value the account refused for good is not sent again; a different one is.
  const refused = selectRefusedSettingWrite(state, coordinate);
  if (refused && isEqual(definition.parse(value), definition.parse(refused.value))) return false;
  const synced = selectSyncedSettingValue(state, coordinate);
  if (synced === undefined) {
    return !isEqual(definition.parse(value), builtInDefault(definition, coordinate.deviceType));
  }
  return !isEqual(definition.parse(value), definition.parse(synced));
};

// ---------------------------------------------------------------------------
// The one global cursor: its old per-mode stores are copies the screens read
// ---------------------------------------------------------------------------

const CURSOR_DEVICE_KEY = 'museflow_cursor_visible';

function* copyCursorToOldStores(visible: boolean): Generator<unknown, void, unknown> {
  yield put(asAccountHydration(appActions.setSightReadingCursorVisible(visible)));
  yield put(asAccountHydration(repertoireActions.setCursorVisible(visible)));
  try {
    localStorage.setItem(CURSOR_DEVICE_KEY, String(visible));
  } catch {
    // Storage may be unavailable; the Redux copies still hold the value.
  }
}

// Sight reading's cursor toggle writes its old store; with the flag on it is
// the global cursor.
function* onSightReadingCursorToggled(action: AnyAction): Generator<unknown, void, unknown> {
  if (!isOn() || isAccountHydration(action)) return;
  yield put(
    settingLayerValueSet({ key: 'cursor', layer: 'global', context: {}, value: action.payload }),
  );
}

// ---------------------------------------------------------------------------
// Loading at sign-in
// ---------------------------------------------------------------------------

/**
 * Loads the account's values and puts each where this device keeps it. Never
 * throws: a failed load leaves this device's values in force.
 */
export function* loadAccountSettings(): Generator<unknown, void, unknown> {
  if (!isOn()) return;
  accountStoreAvailable = true;
  retryableFailures = 0;
  try {
    const response = (yield call(axios.get, ACCOUNT_SETTINGS_URL)) as {
      data?: AccountSettingsPayload;
    };
    const entries = fromAccountSettingsPayload(response?.data);
    const state = (yield select()) as SettingsState;
    const pending = selectPendingSettingWrites(state);
    // A change waiting to be sent, or one the account refused, keeps this device's value.
    const isPending = (coordinate: SettingCoordinate) =>
      pending.some((write) => isEqual(write.coordinate, coordinate)) ||
      selectRefusedSettingWrite(state, coordinate) !== undefined;
    const loaded = entries.map((entry) => ({
      ...entry,
      binding: bindingFor(entry.definition, entry.coordinate.layer, contextOf(entry.coordinate)),
    }));
    // The account's values first, so the bound stores' writes below are not sent back.
    yield put(
      accountSettingsLoaded({
        values: loaded.map(({ coordinate, value, binding }) => ({
          coordinate,
          value,
          bound: binding !== undefined,
        })),
      }),
    );
    const hydrations = loaded
      .filter(({ coordinate }) => !isPending(coordinate))
      .map(({ binding, value }) => (binding ? binding.write(value) : undefined))
      .filter((write): write is AnyAction => write !== undefined);
    for (const write of hydrations) {
      yield put(asAccountHydration(write));
    }
    const cursor = loaded.find(
      ({ coordinate }) => coordinate.key === 'cursor' && coordinate.layer === 'global',
    );
    if (cursor && !isPending(cursor.coordinate)) {
      yield* copyCursorToOldStores(Boolean(cursor.value));
    }
    yield* saveAccountSettings();
  } catch (error) {
    if (isNotFound(error)) {
      accountStoreAvailable = false;
      Logger.info('Account settings: this backend has no /user-settings; using this device');
      return;
    }
    Logger.warn('Account settings: load failed; using this device', error);
  }
}

// ---------------------------------------------------------------------------
// Sending player changes
// ---------------------------------------------------------------------------

function* queueIfNeeded(
  definition: SettingDefinition<unknown>,
  coordinate: SettingCoordinate,
): Generator<unknown, void, unknown> {
  const state = (yield select()) as SettingsState;
  const value = deviceValueAt(state, definition, coordinate);
  if (value === undefined || !needsSending(state, definition, coordinate, value)) return;
  yield put(accountSettingsQueued({ writes: [{ coordinate, value: definition.parse(value) }] }));
}

function* onBoundSettingAction(action: AnyAction): Generator<unknown, void, unknown> {
  if (!isOn() || isAccountHydration(action)) return;
  const watched = watchedSettingActions().filter(({ actionType }) => actionType === action.type);
  for (const { definition, coordinateOf } of watched) {
    const coordinate = coordinateOf(action);
    if (coordinate) yield* queueIfNeeded(definition, coordinate);
  }
}

function* onSettingLayerValueSet(
  action: AnyAction & { payload: SetSettingLayerValuePayload },
): Generator<unknown, void, unknown> {
  if (!isOn() || isAccountHydration(action)) return;
  const { key, layer, context } = action.payload;
  const definition = accountDefinitions().find((candidate) => candidate.key === key);
  if (!definition) return;
  const coordinate = layerCoordinate(key, layer as PlayerLayer, context);
  yield* queueIfNeeded(definition, coordinate);
  if (key === 'cursor' && layer === 'global') {
    yield* copyCursorToOldStores(Boolean(action.payload.value));
  }
}

type SaveOutcome = 'saved' | 'retry' | 'unavailable';

const describeCoordinate = ({ key, layer, mode, instanceId, deviceType }: SettingCoordinate) =>
  [key, layer, mode, instanceId, deviceType].filter((part) => part !== undefined).join(' ');

function* setAside(
  write: SettingCoordinateValue,
  status: number,
  error: unknown,
): Generator<unknown, void, unknown> {
  yield put(
    accountSettingsRefused({ writes: [write], status, refusedAt: new Date().toISOString() }),
  );
  const where = describeCoordinate(write.coordinate);
  Logger.warn(
    `Account settings: the account refused ${where} (${status}); kept on this device only`,
    { value: write.value, response: axios.isAxiosError(error) ? error.response?.data : undefined },
  );
  reportError(new Error(`${status} refused ${where}`), 'accountSettings.refused');
}

/**
 * Sends a batch. On a refusal no try can fix, splits it and sends each half,
 * so the values the account takes are saved and only a value refused on its
 * own is set aside. Stops at the first failure a later try can fix: whatever
 * was not saved stays queued.
 */
function* sendWrites(
  writes: readonly SettingCoordinateValue[],
): Generator<unknown, SaveOutcome, unknown> {
  try {
    yield call(axios.put, ACCOUNT_SETTINGS_URL, toAccountSettingsPayload(writes));
    yield put(accountSettingsConfirmed({ writes: [...writes] }));
    return 'saved';
  } catch (error) {
    if (isNotFound(error)) return 'unavailable';
    const status = permanentRefusalStatus(error);
    if (status === undefined) {
      Logger.warn('Account settings: save failed; kept on this device for the next try', error);
      return 'retry';
    }
    if (writes.length === 1) {
      yield* setAside(writes[0], status, error);
      return 'saved';
    }
    const middle = Math.ceil(writes.length / 2);
    const first = yield* sendWrites(writes.slice(0, middle));
    if (first !== 'saved') return first;
    return yield* sendWrites(writes.slice(middle));
  }
}

/**
 * Sends every queued change in one request. On success the account's copy is
 * recorded. A value the account refuses for good is set aside and the rest are
 * saved (sendWrites). On a failure a later try can fix, the changes stay queued
 * and are tried again after a growing wait, and at the next change or sign-in.
 */
export function* saveAccountSettings(): Generator<unknown, void, unknown> {
  if (!isOn() || !accountStoreAvailable) return;
  const pending = (yield select(selectPendingSettingWrites)) as SettingCoordinateValue[];
  if (pending.length === 0) return;
  const outcome = yield* sendWrites(pending);
  if (outcome === 'unavailable') {
    accountStoreAvailable = false;
    return;
  }
  if (outcome === 'saved') {
    retryableFailures = 0;
    return;
  }
  const wait = ACCOUNT_SETTINGS_RETRY_DELAYS_MS[retryableFailures];
  retryableFailures += 1;
  if (wait !== undefined) yield put(retryScheduled(wait));
}

// A newer schedule replaces a waiting one.
function* retryAfterDelay(action: AnyAction): Generator<unknown, void, unknown> {
  yield delay((action.payload as { delayMs: number }).delayMs);
  yield call(saveAccountSettings);
}

// Worked out on the first action, not when this module loads, so loading it
// never calls an action creator (some suites mock the action modules).
let watchedTypes: ReadonlySet<string> | undefined;

const isWatchedSettingAction = (action: AnyAction): boolean => {
  watchedTypes =
    watchedTypes ?? new Set(watchedSettingActions().map(({ actionType }) => actionType));
  return watchedTypes.has(action.type);
};

export default [
  takeEvery(isWatchedSettingAction, onBoundSettingAction),
  takeEvery(settingLayerValueSet.type, onSettingLayerValueSet),
  takeEvery(actionTypes.SET_SIGHT_READING_CURSOR_VISIBLE, onSightReadingCursorToggled),
  debounce(ACCOUNT_SETTINGS_SAVE_DELAY_MS, accountSettingsQueued.type, saveAccountSettings),
  takeLatest(ACCOUNT_SETTINGS_RETRY_SCHEDULED, retryAfterDelay),
];
